import React from 'react';
import NextHead from 'next/head';
import PropTypes from 'prop-types';

// Data
const twitterCreatorContent = process.env.NEXT_PUBLIC_TWITTER_CREATOR_CONTENT;
const twitterSiteContent = process.env.NEXT_PUBLIC_TWITTER_SITE_CONTENT;
const websiteDescription = process.env.NEXT_PUBLIC_WEBSITE_DESCRIPTION;
const websiteTitle = process.env.NEXT_PUBLIC_WEBSITE_TITLE;
const websiteURL = process.env.NEXT_PUBLIC_WEBSITE_URL;

const Head = ({
  canonicalUrl,
  description = websiteDescription,
  favicon,
  isCdnLink = false,
  keywords,
  noIndex = false,
  pagePath = '',
  shareImage = 'share.jpg',
  structuredData,
  title = websiteTitle
}) => {
  const fullUrl = canonicalUrl || `${websiteURL}/${pagePath}`;

  // If not restaurant favicon is provided, use the default favicon
  const resolvedFavicon = favicon || '/favicon/favicon.ico';

  return (
    <NextHead>
      <meta
        key="viewport"
        content="width=device-width, initial-scale=1, shrink-to-fit=no, maximum-scale=1"
        name="viewport"
      />
      <title>
        {title}
      </title>
      {resolvedFavicon && (
        <>
          <link
            key="icon"
            href={resolvedFavicon}
            rel="icon"
            type="image/x-icon"
          />
          <link
            key="shortcut-icon"
            href={resolvedFavicon}
            rel="shortcut icon"
            type="image/x-icon"
          />
          <link
            key="apple-touch-icon"
            href={resolvedFavicon}
            rel="apple-touch-icon"
          />
        </>
      )}
      <meta
        key="description"
        content={description}
        name="description"
      />
      {keywords && (
        <meta
          key="keywords"
          content={keywords}
          name="keywords"
        />
      )}
      {noIndex && (
        <meta
          key="robots"
          content="noindex, nofollow"
          name="robots"
        />
      )}
      <link
        key="canonical"
        href={fullUrl}
        rel="canonical"
      />
      <meta
        key="og-url"
        content={fullUrl}
        property="og:url"
      />
      <meta
        key="og-title"
        content={title}
        property="og:title"
      />
      <meta
        key="og-site_name"
        content={title}
        property="og:site_name"
      />
      <meta
        key="og-description"
        content={description}
        property="og:description"
      />
      <meta
        key="og-type"
        content="website"
        property="og:type"
      />
      <meta
        key="og-image"
        content={isCdnLink ? `${shareImage}` : `${websiteURL}/open-graph/${shareImage}`}
        property="og:image"
      />
      <meta
        key="twitter-card"
        content="summary_large_image"
        name="twitter:card"
      />
      <meta
        key="twitter-image"
        content={isCdnLink ? `${shareImage}` : `${websiteURL}/twitter-card/${shareImage}`}
        name="twitter:image"
      />
      <meta
        key="twitter-site"
        content={twitterSiteContent}
        name="twitter:site"
      />
      <meta
        key="twitter-creator"
        content={twitterCreatorContent}
        name="twitter:creator"
      />
      <meta
        key="twitter-title"
        content={title}
        name="twitter:title"
      />
      <meta
        key="twitter-description"
        content={description}
        name="twitter:description"
      />
      {structuredData && (
        <script
          key="structured-data"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          type="application/ld+json"
        />
      )}
      <link
        href={`${websiteURL}/sitemap.xml`}
        rel="sitemap"
        type="application/xml"
      />
    </NextHead>
  );
};

Head.propTypes = {
  canonicalUrl: PropTypes.string,
  description: PropTypes.string,
  favicon: PropTypes.string,
  isCdnLink: PropTypes.bool,
  keywords: PropTypes.string,
  noIndex: PropTypes.bool,
  pagePath: PropTypes.string,
  shareImage: PropTypes.string,
  structuredData: PropTypes.shape({}),
  title: PropTypes.string
};

export default Head;
